import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/services';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { AuthError } from '@supabase/supabase-js';
import { Spinner } from '@/components/ui/spinner';
import { useTranslation } from 'react-i18next';
import useHashSessionManager from '@/hooks/useAuthHash';

export default function OTPVerification() {
  const [token, setToken] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useHashSessionManager();

  const handleVerify = useCallback(
    async function (token: string[]) {
      try {
        const otpString = token.join('');
        setIsVerifying(true);
        if (otpString.length === 6) {
          const redirect = searchParams.get('redirect_to');
          const tokenHash = searchParams.get('token_hash');
          if (tokenHash) {
            const { error } = await supabase.auth.verifyOtp({
              token_hash: tokenHash!,
              type: 'email',
            });
            if (error) {
              throw error;
            }
          }
          const type = searchParams.get('type');
          if (type === 'signin') {
            const email = searchParams.get('email');
            const { error } = await supabase.auth.verifyOtp({
              email: email!,
              token: otpString,
              type: 'email',
            });
            if (error) {
              throw error;
            }
          }

          setIsVerifying(false);
          setIsSuccess(true);
          setTimeout(() => {
            if (redirect) {
              navigate(redirect);
            } else {
              navigate('/');
            }
          }, 1500);
        } else {
          alert(t('enterValidOTP'));
        }
      } catch (error: unknown) {
        if (error instanceof AuthError) {
          setError(error.message!);
        }
        setIsVerifying(false);
        setIsSuccess(false);
      }
    },
    [searchParams, navigate, t]
  );

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
    if (!searchParams.get('type')) {
      navigate('/login');
    }
    if (searchParams.get('token')) {
      const tokens = searchParams.get('token')!.split('');
      if (tokens.length === 6) {
        setToken(searchParams.get('token')!.split(''));
        if (formRef.current) {
          handleVerify(tokens);
        }
      }
    }
  }, [searchParams, navigate, handleVerify]);

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).split('');
    const updatedToken = [...token];
    pastedData.forEach((value, index) => {
      if (index < 6) {
        updatedToken[index] = value;
      }
    });
    setToken(updatedToken);
    if (inputRefs.current[pastedData.length - 1]) {
      inputRefs.current[pastedData.length - 1]?.focus();
    }
    if (updatedToken.length === 6) {
      handleVerify(updatedToken);
    }
  };

  const handleChange = (element: HTMLInputElement, index: number) => {
    if (isNaN(Number(element.value))) return false;

    setToken([...token.map((d, idx) => (idx === index ? element.value : d))]);

    // Focus next input
    if (element.value !== '') {
      if (index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (index > 0 && token[index] === '' && inputRefs.current[index - 1]) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = token.join('');
    if (otpString.length === 6) {
      handleVerify(token);
    } else {
      alert(t('enterValidOTP'));
    }
  };

  async function handleResend() {
    const email = searchParams.get('email');
    const type = searchParams.get('type');
    if (type === 'signin') {
      await supabase.auth.signInWithOtp({
        email: email!,
        options: { emailRedirectTo: window.location.origin + '/verify' },
      });
      toast({
        title: t('success'),
        description: t('newCodeSent'),
      });
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            {t('verifyYourAccount')}
          </CardTitle>
          <CardDescription className="text-center">{t('otpSentMessage')}</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit} ref={formRef}>
          <CardContent>
            <AnimatePresence mode="wait">
              {!isSuccess ? (
                <motion.div
                  key="otp-input"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex justify-center gap-2 max-w-sm w-full"
                  onPaste={handlePaste}
                >
                  {isVerifying ? (
                    <Spinner className="w-12 h-12 text-primary" />
                  ) : (
                    token.map((data, index) => (
                      <Input
                        key={index}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="\d{1}"
                        maxLength={1}
                        className="w-12 h-12 text-center text-2xl"
                        value={data}
                        onChange={(e) => handleChange(e.target, index)}
                        onKeyDown={(e) => handleBackspace(e, index)}
                        ref={(input) => (inputRefs.current[index] = input)}
                        aria-label={t('otpDigit', { index: index + 1 })}
                        disabled={isVerifying}
                      />
                    ))
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="success-message"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  className="flex flex-col items-center justify-center space-y-4"
                >
                  <CheckCircle className="w-16 h-16 text-green-500" />
                  <p className="text-xl font-semibold text-green-500">
                    {t('verificationSuccessful')}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full">
              {t('submit')}
            </Button>
            <Button
              type="button"
              onClick={handleResend}
              variant="link"
              className="text-sm"
            >
              {t('resendCode')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
